import '@angular/common/locales/global/nl-BE'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import { IUser } from "redwood-model/dist"
import {REDWOOD_USER} from "../shared-worker-on-ui-thread";
import {PARAMS} from "../PARAMS";
import {getSession} from "../amplify/amplify";
import {graphqlRequest} from "../graphqlHelper";
import {showToast, TOAST_LEVELS} from "../../toast";
import {logError} from "../../log";
import {USER_MAPPING_INFO_KEY} from "../../main";
import {
  asyncPasswordValidator, isUniqueValidator,
  isValidRegimentalNumber,
  passwordMatchValidator,
  regimentalNumberValidator
} from "../validators";
import {hideSpinner, showSpinner} from "../../spinner";
import { AutocompleteComponent } from 'angular-ng-autocomplete';
import { tryGetTokenFromAmplifyState } from '../amplify/amplify_state';
import {GoingBackService} from "../GoingBackService";
import { BsModalService } from 'ngx-bootstrap/modal';
import { sendVerifyEmail } from 'src/redwood';
import { EmailValidatorCodeComponent } from '../email-validator-code/email-validator-code.component';
import { TRANSLATION } from '../amplify/amplify_translations';

const GENDER = {
  "m": "Man",
  "f": "Vrouw"
}
interface IPostalcode {
  zip: string,
  city: string,
  combined?: string
}
interface ICountry {
  name: string,
  code: string
}
interface IschoolBasic { name: string, id: number }
interface IclassBasic { name: string, id: number | undefined }
interface IpreFill {
  username?: string,
  email?: string,
  alias?: string,
  family_name?: string,
  first_name?: string,
  school_role?: string,
  stamboek?: string
}

const role_map: any =
  {
    "Leerling": "ROLE_STUDENT",
    'Leerkracht': "ROLE_TEACHER",
    "School admin": "ROLE_ADMIN",
    "Secretariaat": "ROLE_OFFICE",
  }

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss']
})
export class AddUserComponent implements OnInit, OnDestroy, AfterViewInit {
  formLoading = false
  profileForm: FormGroup | undefined
  user: IUser | undefined = undefined
  genders: string[] = [ GENDER.m, GENDER.f ] // Options for the "Geslacht" select field
  selectedGender = ""
  selectedPostalCode: IPostalcode | undefined
  initialPostal: IPostalcode | undefined
  postalData: IPostalcode[] = []
  postalCodes: IPostalcode[] = []
  countries: ICountry[] = []
  selectedCountry = "BE"
  locales = [
    { name: "Nederlands (België)", value: "nl-BE" },
    { name: "Nederlands (Nederland)", value: "nl-NL"},
    { name: "Frans (België)", value: "fr-BE"},
    { name: "Frans (Frankrijk)", value: "fr-FR"},
    { name: "Spaans (Spanje)", value: "es-ES"},
    { name: "English (UK)", value: "en-UK"},
    { name: "English (US)", value: "en-US"},
  ]
  selectedLocale: any
  selectedRole = 'ROLE_STUDENT';
  schoolsLoading = false;
  schoolError: any = null
  selectedSchool: IschoolBasic | undefined = undefined
  selectedSchoolId: number | undefined = undefined
  schoolOptions: IschoolBasic[] = []
  schoolData: IschoolBasic[] = []
  initialSchool: IschoolBasic | undefined
  selectedClassId: string | undefined = undefined
  unselectedClass: IclassBasic = { id: undefined, name: 'selecteer een klas'}
  classOptions: IclassBasic[] = []
  classLoading = false;
  classError: any = null
  amplify_user: any
  pwMismatch = false
  setSchoolAfterView = false
  stamboekData = ''
  @Input('isMapping') isMapping: boolean = false
  @Output() finalizeNewUser = new EventEmitter<string>()
  @ViewChild(AutocompleteComponent) schoolAutoComplete!: AutocompleteComponent;

  constructor(private formBuilder: FormBuilder,
              private bsModalService: BsModalService,
  ) {
  }

  ngOnDestroy() {
  }

  async ngOnInit() {
    this.formLoading = true
    showSpinner('Formulier data aan het laden')
    try {
      //
      const postalRequest = await fetch('/assets/postalcodes.json')
      const postalData = await postalRequest.json()
      this.postalCodes = postalData.map((pData: {zip: string, city: string, combined: string}) => {
        pData["combined"] = `${pData.zip} ${pData.city}`
        return pData
      })
      const countryRequest = await fetch('/assets/countries.json')
      this.countries = await countryRequest.json()

      if (this.isSchoolAdmin) {
        this.schoolOptions = await this.fetchSchoolQuery();
        if (this.schoolOptions.length) this.selectedSchool = this.schoolOptions[0];
        if (this.selectedSchool) {
          const options= await this.fetchClassQuery();
          this.classOptions = [this.unselectedClass, ...options]
          this.selectedSchoolId = this.selectedSchool.id;
        }
      }
      let username, email, alias, family_name, first_name, school_role, stamboek;
      if (this.isMapping) {
        ({ username, email, alias, family_name, first_name, school_role, stamboek } = await this.prefillRegistrationFormFromClaims());
        this.stamboekData = '' + stamboek
      }


      this.profileForm = this.formBuilder.group({
        gebruikersnaam: [username, [Validators.required, Validators.maxLength(128)], [isUniqueValidator()]],
        emailadres: [email, [Validators.required, Validators.email, Validators.maxLength(255)], [isUniqueValidator()]],
        password: [
          undefined,
          this.isMapping ? [Validators.required, Validators.maxLength(256)] : [], // Sync validators
          this.isMapping ? [asyncPasswordValidator()] : [] // Async validators
        ],
        pwConfirm: [
          undefined,
          this.isMapping ? [Validators.required, passwordMatchValidator()] : [] // Sync validators
        ],
        voornaam: [first_name, [Validators.required, Validators.maxLength(100)]],
        familienaam: [family_name, [Validators.required, Validators.maxLength(100)]],
        alias: [alias],
        ...(this.isMapping || this.isAdmin ? {} : { school: [undefined] }), // Conditionally add 'school'
        klas: [undefined],
        school_role: [school_role],
        ...(school_role === 'ROLE_TEACHER'
          ? {
            stamboek: this.isMapping
              ? [
                '' + stamboek,
                [Validators.maxLength(11), Validators.minLength(11), regimentalNumberValidator()],
              ]
              : [undefined],
          }
          : {}),
      });

      if (username) this.profileForm.get('gebruikersnaam')?.updateValueAndValidity();
      if (email) this.profileForm.get('emailadres')?.updateValueAndValidity();
      if (stamboek) this.profileForm.get('stamboek')?.updateValueAndValidity();
    }
    catch (err) {
      logError(err)
    }
    finally {
      this.formLoading = false;
      hideSpinner()
    }

  }

  async ngAfterViewInit() {
    if (this.isMapping) {
      const waitForSchool = async () => {
        while (!this.selectedSchool?.name && !this.schoolAutoComplete?.searchInput?.nativeElement) {
          await new Promise((resolve) => setTimeout(resolve, 300)); // Wait for 0.3 second before rechecking
        }
      };
      await waitForSchool();
      setTimeout(() => {
        if (this.selectedSchool?.name && this.schoolAutoComplete?.searchInput) {
          this.schoolAutoComplete.searchInput.nativeElement.value = this.selectedSchool.name;
        }

        if (this.profileForm?.get('gebruikersnaam')) {
          this.profileForm.get('gebruikersnaam')?.markAsDirty();
        }
        if (this.profileForm?.get('emailadres')) {
          this.profileForm.get('emailadres')?.markAsDirty();
        }
        if (this.profileForm?.get('stamboek')) {
          this.profileForm.get('stamboek')?.markAsDirty();
        }
      }, 1000)

    }
    this.rolesChanged()
  }

  rolesChanged() {
    const school_role = this.profileForm?.get('school_role')
    if  (school_role?.value === 'ROLE_TEACHER') {
      this.profileForm?.addControl(
        'stamboek',
        this.isMapping
          ? new FormControl('' + this.stamboekData, [
            Validators.maxLength(11),
            Validators.minLength(11),
            regimentalNumberValidator()
          ])
          : new FormControl(undefined)
      );
      setTimeout(() => {
        this.profileForm?.get('stamboek')?.updateValueAndValidity();
      }, 100)

    } else {
      this.profileForm?.removeControl('stamboek')
    }
  }

  async prefillRegistrationFormFromClaims(): Promise<IpreFill> {
    const returnObject: IpreFill = {}
      try {
        if (localStorage[USER_MAPPING_INFO_KEY]) {
          const parsed = JSON.parse(localStorage["userMappingInfo"])
          const userInfo = parsed?.userInfo
          if (parsed.pool.includes("smartschool")) {
            if (userInfo) {
              const username = userInfo.username || userInfo.userInfo.actualUserName
              await this.setSchoolRelatedData(userInfo)
              if (username) {
                returnObject['username'] = username
              }
              if (userInfo?.email) {
                returnObject['email'] = userInfo?.email
              }
              if (userInfo.nickname) {
                returnObject['alias'] = userInfo.nickname
              }
              if (userInfo.surname) {
                returnObject['family_name'] = userInfo.surname
              }
              if (userInfo.name) {
                returnObject['first_name'] = userInfo.name
              }
              if (userInfo.nickname) {
                returnObject['alias'] = userInfo.nickname
              }
              if (userInfo.stamboeknummer && userInfo.stamboeknummer !== 'NULL') {
                returnObject['stamboek'] = userInfo.stamboeknummer
              }
              if (role_map[userInfo.basisrol]) {
                returnObject['school_role'] = role_map[userInfo.basisrol]
                this.selectedRole = role_map[userInfo.basisrol]
              }
            }
          } else if (parsed.pool.includes("leer")) {
            if (userInfo.given_name) {
              returnObject['first_name'] = userInfo.given_name
            }
            if (userInfo.family_name) {
              returnObject['family_name'] = userInfo.family_name
            }
          }
        }

      } catch (e) {
        logError("couldn't parse JSON localStorage.userMappingInfo")
      } finally {

        return returnObject
      }
  }
  extractSchool (input:string) {
    try {
      let hostname = new URL(input).hostname;
      let parts = hostname.split('.');

      if (parts.length > 2) {
        return parts.slice(0, -2).join('.');
      }

      return null;
    } catch (e) {
      return null; // Invalid URL
    }
  }

  async setSchoolRelatedData(userInfo: any) {
    if (!userInfo?.platform) return;

    const school = this.extractSchool(userInfo.platform);
    if (!school) return;

    // Fetch or create school data
    this.schoolData = await this.fetchSchoolSearch(school);
    if (!this.schoolData?.[0]?.id) {
      const newSchool = await this.addSchoolFromClaims(school);
      if (newSchool) this.schoolData.unshift(newSchool);
    }

    if (!this.schoolData?.[0]?.id) return;

    // Override 'Diligentia' school
    // if (this.schoolData[0].name === 'Diligentia') {
    //   this.schoolData[0] = { id: 1072, name: 'demoschool' };
    // }

    this.selectedSchool = this.schoolData[0];
    this.selectedSchoolId = this.selectedSchool.id;

    const searchInput = this.schoolAutoComplete?.searchInput?.nativeElement;
    if (searchInput) {
      searchInput.value = this.selectedSchool.name;
    } else {
      this.setSchoolAfterView = true;
    }

    this.classOptions = [this.unselectedClass, ...(await this.fetchClassQuery())];

    this.selectedClassId = this.findMatchingClass(userInfo?.groups?.groups);

    // If no class is found, create one from claims
    try {
      if (!this.selectedClassId) {
        const newClass = await this.addClassFromClaims(userInfo.groups.groups);
        if (newClass) {
          this.classOptions.push(newClass);
          this.selectedClassId = '' + newClass.id;
        }
      }
    } catch (err) {
      logError(err)
    }
  }

  private findMatchingClass(groups: any[] = []): string | undefined {
    for (const option of this.classOptions) {
      for (const group of groups) {
        if (
          option.name.toLowerCase() === group.name.toLowerCase() ||
          option.name.toLowerCase() === group.code.toLowerCase()
        ) {
          return '' + option.id;
        }
      }
    }
    return undefined;
  }
  async addClassFromClaims(groups: any[]): Promise<IclassBasic|undefined> {
    const klas = (groups.filter((group:any) => group.name !== 'Leerkrachten' && group.name !== 'Leerlingen'))[0]

    if  (!klas) {
      throw new Error('no class in claims');
    }
    const extractFirstNum = (input:string) => {
      const match = input.match(/\d+/);
      return match ? parseInt(match[0], 10) : null;
    }

    try {
      const token = this.amplify_user?.signInUserSession?.accessToken?.jwtToken || (await getSession()).access_token

      const speciality = klas['description']
      const year = extractFirstNum(klas['code'])
      let schoolPlatform = klas['platform']
      const name = klas['name']


      if  (!speciality || !year || !schoolPlatform || !name) {
        throw new Error('No speciality or year or name');
      }

      const query = `
      mutation {
          insertNewSchoolClass(
              input: {
                  ${speciality ? `speciality: "${speciality}"` : '' }
                  ${year ? `year: ${+year}` : '' }
                  ${schoolPlatform ? `schoolPlatform: "${schoolPlatform}"` : '' }
                  ${name ? `name: "${name}"` : '' }
              }
          ){
            id
          }
      }`

      const {data, errors} = await graphqlRequest({url: PARAMS["REDWOOD-LAMBDA"] + '/query', query, token})
      if (errors.length) {
        throw new Error(errors.map((err: any) => err.message).join(', '))
      }

      const { id } = data?.data?.insertNewSchoolClass

      return { id , name}

    } catch (err) {
      logError(err)
      return undefined
    }


  }
  async addSchoolFromClaims(school: string): Promise<IschoolBasic|undefined> {
    try {
      const token = this.amplify_user?.signInUserSession?.accessToken?.jwtToken || (await getSession()).access_token
      const name = school

      const query = `
      mutation {
          insertNewSchool(
              input: {
                  ${name ? `name: "${name}"` : '' }
                  phone: " "
                  address: " "
                  postal: " "
                  city: " "
                  country: " "
                  email: " "
                  website: " "
                  school_nr: 0000
              }
          ){
            id
          }
      }`

      const {data, errors} = await graphqlRequest({url: PARAMS["REDWOOD-LAMBDA"] + '/query', query, token})
      if (errors.length) {
        showToast(errors[0].message, 'error')
        throw new Error(errors.map((err: any) => err.message).join(', '))
      }

      const { id } = data.data['insertNewSchool']

      return { id , name}

    } catch (err) {
      logError(err)
      return undefined
    }
  }


  async fetchSchoolQuery (): Promise<IschoolBasic[]> {
    const redwoodUser = REDWOOD_USER()
    const token = tryGetTokenFromAmplifyState(localStorage)
    let schoolsArray: IschoolBasic[] = []
    if (!token) throw new Error('user not authenticated')
    try {
      this.schoolsLoading = true;
      const query = `
      query userByIdQuery($id: Int!) {
            userByIdQuery(id: $id) {
                schoolUsers {
                    school {
                        id
                        name
                    }
                }
            }
        }`
      const variables = { id: Number(redwoodUser.id) };

      const operationName = 'userByIdQuery'

      const {data, errors} = await graphqlRequest( {
        url: PARAMS["REDWOOD-LAMBDA"]+'/query',
        query,
        operationName,
        token,
        variables
      })

      if (errors.length) {
        throw new Error(errors.map((err: any) => err.message).join(', '));
      }
      schoolsArray = data?.data?.userByIdQuery?.schoolUsers
        ?.map((su: { school: IschoolBasic }) => su.school)
        ?.filter((school: IschoolBasic, index: number, self: []) =>
          index === self.findIndex((s: IschoolBasic) => s.id === school.id)
        );

    } catch (err) {
      this.schoolError = err

    } finally {
      this.schoolsLoading = false;

      return schoolsArray

    }
  }
  async schoolChanged(event: Event ) {
    const {value} = (event?.target as HTMLSelectElement)
    if (value) {
      this.selectedSchoolId = +value
      this.selectedSchool = this.schoolOptions.find(s => s.id === this.selectedSchoolId)
      const options = await this.fetchClassQuery();
      this.classOptions = [this.unselectedClass, ...options]
    }
  }
  async fetchClassQuery (): Promise<IclassBasic[]> {
    let classArray: IclassBasic[] = []
    try {

      if (!this.selectedSchool?.id) throw new Error('No school selected')

      const token = tryGetTokenFromAmplifyState(localStorage)

      if (!token) throw new Error('User not authenticated')

      this.classLoading = true;
      const query = `
      query classesForSchoolQuery($school_id: Int!) {
            classesForSchoolQuery(school_id: $school_id) {
                id
                name
            }
        }`
      const variables = { school_id: Number(this.selectedSchool?.id) };

      const operationName = 'classesForSchoolQuery'

      const {data, errors} = await graphqlRequest({
        url: PARAMS["REDWOOD-LAMBDA"] + '/query',
        query,
        operationName,
        token,
        variables
      })

      if (errors.length) {
        throw new Error(errors.map((err: any) => err.message).join(', '));
      }
      classArray = data?.data?.classesForSchoolQuery
        ?.filter((klas: IclassBasic, index: number, self: []) =>
          index === self.findIndex((c: IclassBasic) => c.id === klas.id)
        );

    } catch (err) {
      this.classError = err

    } finally {
      this.classLoading = false;

      return classArray

    }
  }
  clearClasses() {
    this.classOptions = [this.unselectedClass]
    this.selectedClassId = undefined
  }

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault()

    const token = tryGetTokenFromAmplifyState(localStorage)

    if (!this.profileForm?.value?.gebruikersnaam) return;
    if (!this.isValid) return;
    if (!token) throw new Error('User not authenticated')

    const {
      emailadres: email,
      gebruikersnaam: username,
      voornaam: first_name,
      familienaam: last_name,
      alias,
      password,
      klas: school_class_id,
      school_role
    } = this.profileForm.value;

    const finalizeSubmit = async () => {
      let school_id = this.profileForm?.value?.school;

      if (!school_id && this.selectedSchool) {
        school_id = +this.selectedSchool.id
      }

      const query = `
        mutation {
            insertNewSchoolUser(
                input: {
                    email: "${email}"
                    username: "${username}"
                    first_name: "${first_name}"
                    last_name: "${last_name}"
                    alias: "${alias}"
                    school_role: "${school_role}"
                    school_id: ${+school_id}
                    ${school_class_id ? `school_class_id: ${+school_class_id}` : '' }
                    ${password ? `password: "${password}"` : '' }
                }
            ){
              id
            }
        }`
      const {data, errors} = await graphqlRequest({url: PARAMS["REDWOOD-LAMBDA"] + '/query', query, token})
      if (errors.length) {
        showToast(errors[0].message, 'error')
        throw new Error(errors.map((err: any) => err.message).join(', '))
      }

      const { id } = data.data['insertNewSchoolUser']

      if (id && !errors.length) {
        if (this.isMapping) {
          showSpinner(`Gebruiker met gebruikersnaam: ${username} werd aangemaakt! U wordt nu uitgelogd.`)
          this.finalizeNewUser.emit(username)
          return
        }
        showToast(`Gebruiker met gebruikersnaam: ${username} werd aangemaakt!`, TOAST_LEVELS.SUCCES)

        GoingBackService.handleBack()
      }
    }

    if (this.isMapping) {
      await sendVerifyEmail(email, first_name)
      const DIALOG_OPEN_OPTIONS = {
        ignoreBackdropClick: true,
        initialState: { email }
      }
      const modalRef = this.bsModalService.show(EmailValidatorCodeComponent, DIALOG_OPEN_OPTIONS)
      const onCodeVerifiedSubscription = modalRef.content?.OnCodeVerified$.subscribe(async (succeeded: any) => {
        if (!succeeded) {
          showToast(succeeded.error, TOAST_LEVELS.ERROR, TRANSLATION.ERROR_TITLE)
          return
        }
        await finalizeSubmit()
        onCodeVerifiedSubscription?.unsubscribe()
      })
    } else {
      await finalizeSubmit()
    }

  }
  cancel() {
    GoingBackService.handleBack()
  }

  selectPostalcode($event: IPostalcode) {
    this.selectedPostalCode = $event
  }

  searchPostalCode($event: string) {
    this.initialPostal = undefined
    this.postalData = [...this.postalCodes.filter(pc => pc.zip.startsWith($event))]
  }
  selectedPostalRender(pc: any) : string {
    return `${pc.zip} - ${pc.city}`
  }

  async selectSchool($event: IschoolBasic) {
    this.selectedSchool = $event
    this.selectedSchoolId = this.selectedSchool.id
    const options = await this.fetchClassQuery();
    this.classOptions = [this.unselectedClass, ...options]
  }

  async searchSchools($event: string) {
    this.initialSchool = undefined
    this.schoolData = await this.fetchSchoolSearch($event)
  }
  async schoolKeyUp($event: KeyboardEvent) {
    const value = ($event?.target as HTMLInputElement)?.value
    if (this.selectedSchool && value.toLowerCase() !== this.selectedSchool.name.toLowerCase()) {
      this.selectedSchool = undefined
      this.selectedSchoolId = undefined
    }
  }

  async fetchSchoolSearch(input: string) : Promise<IschoolBasic[]> {

    let schoolsArray: IschoolBasic[] = []

    if (input.length < 1) return schoolsArray

    try {
      const token = tryGetTokenFromAmplifyState(localStorage)

      if (!token) throw new Error('user not authenticated')
        this.schoolsLoading = true;
        const query = `
      query schoolsQuery($prefix: String!) {
            schoolsQuery(prefix: $prefix) {
                id
                name
            }
        }`
        const variables = { prefix: String(input) };

        const operationName = 'schoolsQuery'

        const {data, errors} = await graphqlRequest({
          url: PARAMS["REDWOOD-LAMBDA"] + '/query',
          query,
          operationName,
          token,
          variables
        })

        if (errors.length) {
          throw new Error(errors.map((err: any) => err.message).join(', '));
        }
        schoolsArray = data?.data?.schoolsQuery
          ?.filter((school: IschoolBasic, index: number, self: []) =>
            index === self.findIndex((s: IschoolBasic) => s.id === school.id)
          );
    } catch (err) {
      logError(err)
    } finally {
        return schoolsArray
    }
  }

  selectedSchoolRender(school: IschoolBasic) : string {
    return school.name
  }
  stamboekChanged(event: Event) {
    const value = (event.target as HTMLInputElement).value
    const control = this.profileForm?.get('stamboek');
    if(control) {
      control.setValue(value)
    }
  }

  get noValidSchool(): boolean {

    if (this.selectedRoleNeedsSchool && this.isMapping) {
      if (!this.selectedSchoolId) return true
      return false
    }
    return false

  }
  get noValidClass(): boolean {
    if (this.selectedRoleNeedsSchool && this.isMapping && !this.isMappingTeacher) {
      if (!this.selectedClassId || this.selectedClassId === 'undefined') return true
      return false
    }
    return false

  }
  get noValidSchoolOrClass(): boolean {
    return this.noValidClass || this.noValidSchool
  }

  get formIsDirty(): boolean {
    return !!this.profileForm?.dirty
  }

  get isValid(): boolean {
    if (this.noValidSchoolOrClass){
      return false
    }
    if (this.isMapping) {
      if (this.isMappingTeacher && !isValidRegimentalNumber(''+this.profileForm?.value['stamboek'])) {
        return false
      }
      if (this.profileForm?.value['password'] !== this.profileForm?.value['pwConfirm']) {
        this.pwMismatch = true
        return false
      }
    }
    this.pwMismatch = false

    return !!this.profileForm?.valid
  }
  get isSchoolAdmin(): boolean {
    if (this.isMapping) return false
    const redwood_user: any = REDWOOD_USER()
    return redwood_user.roles.includes('ROLE_SCHOOL_ADMIN')
  }
  get isAdmin(): boolean {
    if (this.isMapping) return false
    const redwood_user: any = REDWOOD_USER()
    return redwood_user.roles.includes('ROLE_ADMIN')
  }
  get isMappingTeacher() {
    if (!this.isMapping) return false

    return this.selectedRole === 'ROLE_TEACHER'
  }
  get selectedRoleNeedsSchool() {
    return ['ROLE_TEACHER', 'ROLE_STUDENT'].indexOf(this.selectedRole) !== -1
  }

  get isInValidSchool() : boolean {
    if (!this.profileForm?.dirty) return false
    const result = !!this.profileForm?.dirty && this.isMapping && this.selectedRoleNeedsSchool && this.noValidSchoolOrClass
    return result
  }
  get isInValidClass() : boolean {
    return !!this.profileForm?.dirty && !this.isMappingTeacher && this.isMapping && this.selectedRoleNeedsSchool && this.noValidSchoolOrClass
  }

  protected readonly role_map = role_map;
  protected readonly Object = Object;
}

